import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSliceBalance } from '../hooks/useSliceBalance';
import { useSlicePot } from '../hooks/useSlicePot';
import CrowdfundCard from '../components/CrowdfundCard';
import BackCampaignModal from '../components/BackCampaignModal';
import CommunityPickBanner from '../components/pot/CommunityPickBanner';
import RisingCampaignsRail from '../components/pot/RisingCampaignsRail';
import CommunityPicksHistory from '../components/pot/CommunityPicksHistory';
import RailHeader from '../components/home/RailHeader';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { contentService, isCampaignPastEndDate } from '../services/contentService';
import { safeUrl } from '../lib/safeUrl';
import {
  ArrowLeft,
  Users,
  Clock,
  CheckCircle2,
  XCircle,
  Play,
  ArrowUpRight,
  CalendarDays,
} from 'lucide-react';

/* ─── helpers ─────────────────────────────────────────────── */

const daysLeft = (endDate) => {
  if (!endDate) return null;
  const diff = new Date(endDate) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const statusMeta = {
  active:    { label: 'In production', dot: 'bg-green-500',   text: 'text-green-500',   Icon: Clock },
  funded:    { label: 'Funded',        dot: 'bg-primary',     text: 'text-primary',     Icon: CheckCircle2 },
  completed: { label: 'Completed',     dot: 'bg-blue-500',    text: 'text-blue-500',    Icon: CheckCircle2 },
  cancelled: { label: 'Cancelled',     dot: 'bg-destructive', text: 'text-destructive', Icon: XCircle },
};

/* ─── Skeletons ────────────────────────────────────────────── */

const CardSkeleton = () => (
  <div className="rounded-lg border border-border/40 overflow-hidden animate-pulse">
    <div className="aspect-[4/5] bg-muted/40" />
    <div className="p-4 space-y-3">
      <div className="h-4 bg-muted rounded w-3/4" />
      <div className="h-3 bg-muted rounded w-1/2" />
      <div className="h-px bg-muted w-full" />
      <div className="flex gap-4">
        <div className="h-3 bg-muted rounded w-20" />
        <div className="h-3 bg-muted rounded w-16" />
      </div>
    </div>
  </div>
);

/* ─── Empty state ──────────────────────────────────────────── */

const EmptyState = ({ title, subtitle }) => (
  <div className="rounded-lg border border-dashed border-border/60 py-16 px-6 text-center">
    <p className="font-semibold text-foreground">{title}</p>
    {subtitle && (
      <p className="text-sm text-muted-foreground mt-1.5">{subtitle}</p>
    )}
  </div>
);

/* ─── Browse view ──────────────────────────────────────────── */

const BrowseView = () => {
  const { isAuthenticated } = useAuth();
  const { summary, worksSummary, pastWinners, isLoading: potLoading } = useSlicePot();

  const [active, setActive]       = useState([]);
  const [funded, setFunded]       = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      const [activeData, fundedData] = await Promise.all([
        contentService.getCrowdfundingProjects({ status: 'active', limit: 50 }),
        contentService.getCrowdfundingProjects({ status: 'funded', limit: 12 }),
      ]);
      setActive(activeData);
      setFunded(fundedData);
      setIsLoading(false);
    };
    load();
  }, []);

  return (
    <div className="space-y-10 px-4 py-8 md:space-y-14 md:px-8 lg:px-10 md:py-10">

      {/* ══ Page header ══ */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Crowdfunding</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Back independent films and series — directly from the creators making them.
        </p>
      </div>

      {/* ══ Community Fund hero banner — always renders ══ */}
      <CommunityPickBanner
        worksSummary={worksSummary}
        isLoading={potLoading}
      />

      {/* ══ Active campaigns ══ */}
      <section>
        <RailHeader title="Active Campaigns" />

        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} />)}
          </div>
        ) : active.length === 0 ? (
          <EmptyState
            title="No active campaigns"
            subtitle="New projects land here regularly — come back soon."
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {active.map((p, i) => <CrowdfundCard key={p.id} project={p} index={i} />)}
          </div>
        )}
      </section>

      {/* ══ Rising campaigns rail ══ */}
      {!potLoading && summary && (
        <RisingCampaignsRail summary={summary} campaigns={active} />
      )}

      {/* ══ Past Community Picks ══ */}
      {!potLoading && (
        <CommunityPicksHistory pastWinners={pastWinners} />
      )}

      {/* ══ Recently funded ══ */}
      {!isLoading && funded.length > 0 && (
        <section>
          <RailHeader title="Recently Funded" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {funded.map((p, i) => <CrowdfundCard key={p.id} project={p} index={i} />)}
          </div>
        </section>
      )}

      {/* ══ How it works — shown to non-subscribers only ══ */}
      {!isAuthenticated && (
        <div className="border-t border-border/40 pt-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold text-foreground">How it works</h3>
              <p className="mt-1 text-sm text-muted-foreground max-w-xl leading-relaxed">
                Subscribers receive monthly slices to pledge however they like. Creators receive
                direct funding for work that wouldn't exist otherwise.
              </p>
            </div>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline shrink-0"
            >
              See pricing
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Detail view ──────────────────────────────────────────── */

const DetailView = ({ id }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { balance, isActiveSubscriber } = useSliceBalance();

  const [project, setProject]         = useState(null);
  const [isLoading, setIsLoading]     = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await contentService.getCrowdfundingProject(id);
      setProject(data);
    } catch {
      setProject(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  /* loading */
  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6 animate-pulse">
        <div className="h-4 bg-muted rounded w-32" />
        <div className="aspect-[16/7] bg-muted rounded-xl" />
        <div className="grid md:grid-cols-[1fr_360px] gap-10">
          <div className="space-y-4">
            <div className="h-8 bg-muted rounded w-2/3" />
            <div className="h-4 bg-muted rounded w-1/3" />
            <div className="h-3 bg-muted rounded w-full" />
            <div className="h-3 bg-muted rounded w-4/5" />
          </div>
          <div className="h-80 bg-muted rounded-lg" />
        </div>
      </div>
    );
  }

  /* not found */
  if (!project) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-24 text-center space-y-5">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Campaign not found</p>
        <h2 className="text-3xl md:text-4xl font-semibold text-foreground">
          This campaign doesn't exist.
        </h2>
        <p className="text-sm text-muted-foreground">
          It may have been removed or taken out of rotation.
        </p>
        <Button
          onClick={() => navigate('/crowdfund')}
          className="rounded-full h-11 px-5"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to campaigns
        </Button>
      </div>
    );
  }

  const pct     = Math.min(project.progressPercentage ?? 0, 100);
  const days    = daysLeft(project.endDate);
  const meta    = statusMeta[project.status] ?? statusMeta.active;
  const canBack = project.status === 'active' && !isCampaignPastEndDate(project.endDate);

  /* ── button logic ── */
  let backButton;
  if (!isAuthenticated) {
    backButton = (
      <Button className="w-full h-12 rounded-full" onClick={() => navigate('/auth')}>
        Sign in to back
      </Button>
    );
  } else if (!isActiveSubscriber) {
    backButton = (
      <Button className="w-full h-12 rounded-full" onClick={() => navigate('/pricing')}>
        Subscribe to back
      </Button>
    );
  } else if (!canBack) {
    backButton = (
      <Button className="w-full h-12 rounded-full" variant="outline" disabled>
        {project.status === 'funded' ? 'Goal reached' : 'Campaign ended'}
      </Button>
    );
  } else {
    backButton = (
      <Button
        className="w-full h-12 rounded-full text-base"
        disabled={balance === 0}
        onClick={() => setIsModalOpen(true)}
      >
        {balance === 0 ? 'No slices available' : '🍕 Back this campaign'}
      </Button>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-10 pb-20 space-y-8">

        {/* Back link */}
        <Link
          to="/crowdfund"
          className="group inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          All campaigns
        </Link>

        {/* ══ Hero image ══ */}
        {(project.backdrop || project.image) && (
          <div className="relative aspect-[16/7] overflow-hidden rounded-xl bg-muted border border-border/40">
            <img
              src={project.backdrop || project.image}
              alt={project.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/10 to-transparent pointer-events-none" />
          </div>
        )}

        {/* ══ Title block ══ */}
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">
              Campaign
            </span>
            <span className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest ${meta.text}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${meta.dot} ${canBack ? 'animate-pulse' : ''}`} />
              {meta.label}
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight max-w-4xl">
            {project.title}
          </h1>

          {project.creator && (
            <div className="flex items-center gap-3">
              <Avatar className="h-8 w-8">
                <AvatarImage src={project.creator.avatar} />
                <AvatarFallback className="text-xs bg-primary/15 text-primary border border-primary/30">
                  {project.creator.name?.charAt(0)?.toUpperCase() ?? 'C'}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm text-muted-foreground">
                directed by{' '}
                <span className="text-foreground font-medium">{project.creator.name}</span>
              </span>
              {project.creator.isVerified && (
                <CheckCircle2 className="h-4 w-4 text-primary" />
              )}
            </div>
          )}
        </div>

        {/* ══ Main layout ══ */}
        <div className="grid md:grid-cols-[1fr_360px] gap-10 lg:gap-16 items-start">

          {/* ── Left column ── */}
          <div className="space-y-10 min-w-0">

            {/* Stats row */}
            <div className="grid grid-cols-2 md:grid-cols-4 rounded-lg border border-border/40 divide-x divide-border/40 overflow-hidden">
              <StatCell
                label="Pledged"
                value={`🍕 ${(project.raised ?? 0).toLocaleString()}`}
              />
              <StatCell
                label="Goal"
                value={`🍕 ${(project.goal ?? 0).toLocaleString()}`}
              />
              <StatCell
                label="Backers"
                value={(project.backerCount ?? 0).toLocaleString()}
                icon={<Users className="h-3.5 w-3.5" />}
              />
              <StatCell
                label={canBack ? 'Days left' : 'Ended'}
                value={canBack && days !== null ? `${days}d` : formatDate(project.endDate)}
                icon={<Clock className="h-3.5 w-3.5" />}
              />
            </div>

            {/* Progress */}
            <div className="space-y-3">
              <div className="flex items-end justify-between">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">Funded</span>
                <span className="text-5xl md:text-6xl font-semibold text-foreground tabular-nums leading-none">
                  {pct}
                  <span className="text-sm text-muted-foreground ml-1">%</span>
                </span>
              </div>
              <div className="relative h-[3px] bg-border/60 w-full overflow-hidden rounded-full">
                <div
                  className="absolute inset-y-0 left-0 bg-primary transition-all duration-700 rounded-full"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>

            {/* Description */}
            {project.description && (
              <section className="space-y-3">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Synopsis</p>
                <p className="text-foreground/85 leading-relaxed text-[1.05rem] whitespace-pre-line max-w-2xl">
                  {project.description}
                </p>
              </section>
            )}

            {/* Associated work */}
            {project.work && (
              <section className="space-y-3">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Associated work</p>

                <div className="rounded-lg border border-border/40 p-4 flex items-center gap-5 hover:border-primary/30 transition-colors">
                  {project.work.poster_url && (
                    <img
                      src={project.work.poster_url}
                      alt={project.work.title}
                      className="h-20 w-14 object-cover rounded shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Watch now</p>
                    <p className="font-semibold text-foreground truncate">{project.work.title}</p>
                  </div>
                  {project.work.slug && (
                    <Link
                      to={`/watch/${project.work.slug}`}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline shrink-0"
                    >
                      View
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </div>
              </section>
            )}

            {/* Campaign updates */}
            {project.updates?.length > 0 && (
              <section className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Updates</p>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {project.updates.length} total
                  </span>
                </div>

                <ol className="divide-y divide-border/40 border border-border/40 rounded-lg overflow-hidden">
                  {project.updates.map((u, i) => (
                    <li key={u.id} className="py-5 px-5 space-y-2">
                      <div className="flex flex-wrap items-baseline gap-3 justify-between">
                        <div className="flex items-baseline gap-3">
                          <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                            #{i + 1}
                          </span>
                          <h3 className="font-semibold text-foreground">
                            {u.title}
                          </h3>
                        </div>
                        <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5 tabular-nums shrink-0">
                          <CalendarDays className="h-3 w-3" />
                          {formatDate(u.created_at)}
                        </span>
                      </div>
                      {u.body && (
                        <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                          {u.body}
                        </p>
                      )}
                    </li>
                  ))}
                </ol>
              </section>
            )}
          </div>

          {/* ── Right column — sticky back panel ── */}
          <aside className="md:sticky md:top-24">
            <div className="rounded-xl border border-border/50 bg-card/40 backdrop-blur-sm p-6 space-y-5">

              {/* Panel header */}
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Back this campaign</p>

              {/* Mini progress */}
              <div className="space-y-2">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-2xl font-semibold text-foreground tabular-nums">
                    🍕 {(project.raised ?? 0).toLocaleString()}
                  </span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    of {(project.goal ?? 0).toLocaleString()}
                  </span>
                </div>
                <div className="relative h-[3px] bg-border/60 w-full overflow-hidden rounded-full">
                  <div
                    className="absolute inset-y-0 left-0 bg-primary rounded-full"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {pct}% funded · {(project.backerCount ?? 0).toLocaleString()} backers
                </p>
              </div>

              {/* Dates */}
              <div className="space-y-1.5 text-xs text-muted-foreground">
                {project.startDate && (
                  <div className="flex justify-between">
                    <span>Opened</span>
                    <span className="text-foreground/80 tabular-nums">{formatDate(project.startDate)}</span>
                  </div>
                )}
                {project.endDate && (
                  <div className="flex justify-between">
                    <span>{canBack ? 'Closes' : 'Closed'}</span>
                    <span className="text-foreground/80 tabular-nums">{formatDate(project.endDate)}</span>
                  </div>
                )}
              </div>

              {/* Balance hint */}
              {isAuthenticated && isActiveSubscriber && canBack && (
                <p className="text-xs text-muted-foreground text-center tabular-nums border-t border-border/40 pt-4">
                  Available · 🍕 {balance ?? 0} slices
                </p>
              )}

              {/* CTA */}
              {backButton}

              {/* Trailer link */}
              {project.trailerUrl && (
                <a
                  href={safeUrl(project.trailerUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full h-10 rounded-full border border-border/60 text-sm text-muted-foreground hover:text-foreground hover:border-border transition-colors"
                >
                  <Play className="h-3.5 w-3.5" />
                  Watch trailer
                </a>
              )}
            </div>
          </aside>
        </div>
      </div>

      {/* Back campaign modal */}
      {isAuthenticated && isActiveSubscriber && (
        <BackCampaignModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          campaign={{ id: project.id, title: project.title }}
          balance={balance ?? 0}
          isActiveSubscriber={isActiveSubscriber}
          onSuccess={load}
        />
      )}
    </>
  );
};

/* ─── Stat cell (detail view) ───────────────────────────────── */
const StatCell = ({ label, value, icon }) => (
  <div className="px-4 py-4 bg-card/20">
    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2 inline-flex items-center gap-1.5">
      {icon}{label}
    </p>
    <p className="text-xl font-semibold text-foreground tabular-nums truncate">
      {value}
    </p>
  </div>
);

/* ─── Page router ──────────────────────────────────────────── */

const Crowdfunding = () => {
  const { id } = useParams();
  return id ? <DetailView id={id} /> : <BrowseView />;
};

export default Crowdfunding;
